import { DEFAULT_PREFERENCES, type LatLng, type UnitSystemPreference } from '../model'

export const FEET_PER_METER = 3.28084
export const FEET_PER_MILE = 5280
export const METERS_PER_MILE = 1609.344

/**
 * @returns meters (rounded) between 2 points
 */
export function distanceInMeters(p1: LatLng, p2: LatLng): number {
  return Math.round(haversineDistance(p1, p2) * 1000)
}

/**
 * @returns meters between user and property, or -1 if either coordinate is missing
 */
export function calculateDistanceFromProperty(userCoordinates?: LatLng, propertyCoordinates?: LatLng): number {
  if (!userCoordinates || !propertyCoordinates) return -1
  return distanceInMeters(userCoordinates, propertyCoordinates)
}

/**
 * @returns miles between 2 points
 */
export function distanceInMiles(p1: LatLng, p2: LatLng): number {
  return haversineDistance(p1, p2) * 0.621371
}

const EARTH_RADIUS_KM = 6371

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

/**
 * @returns kilometers between 2 points
 */
export function haversineDistance(a: LatLng, b: LatLng): number
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number
export function haversineDistance(a: LatLng | number, b: LatLng | number, lat2?: number, lon2?: number): number {
  if (lat2 !== undefined && lon2 !== undefined && typeof a === 'number' && typeof b === 'number')
    return _haversineDistance(a, b, lat2, lon2)
  else if (typeof a === 'object' && typeof b === 'object')
    return _haversineDistance(a.latitude, a.longitude, b.latitude, b.longitude)
  else throw new Error('Invalid input')
}

/**
 * @returns kilometers between 2 points
 */
function _haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS_KM * c
}

export function metersToMiles(meters: number): number {
  return meters / METERS_PER_MILE
}
export function milesToMeters(miles: number): number {
  return miles * METERS_PER_MILE
}
export function metersToFeet(meters: number): number {
  return meters * FEET_PER_METER
}

export function metersToString(meters: number, unitSystem: UnitSystemPreference): string {
  return formatDistance({ meters, unitSystem })
}

type Distance = { miles: number } | { meters: number }
export type FormatDistanceParams = Distance & {
  unitDisplay?: 'narrow' | 'short' | 'long'
  unitSystem: UnitSystemPreference | undefined
}

export function formatDistance(props: FormatDistanceParams): string {
  const unitSystem = props.unitSystem ?? DEFAULT_PREFERENCES.unitSystem
  if (unitSystem === 'imperial') {
    return formatImperialDistance(props)
  }
  return formatMetricDistance(props)
}

function formatImperialDistance({ unitDisplay = 'long', ...distance }: FormatDistanceParams): string {
  const distanceInFeet = 'miles' in distance ? distance.miles * FEET_PER_MILE : metersToFeet(distance.meters)

  if (distanceInFeet <= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'unit',
      unit: 'foot',
      unitDisplay,
      maximumFractionDigits: 0,
    }).format(Math.round(distanceInFeet))
  }

  const distanceInMiles = 'miles' in distance ? distance.miles : metersToMiles(distance.meters)

  return new Intl.NumberFormat('en-US', {
    style: 'unit',
    unit: 'mile',
    unitDisplay,
    maximumFractionDigits: getFractionDigits(distanceInMiles),
  }).format(distanceInMiles)
}

function formatMetricDistance({ unitDisplay = 'long', ...distance }: FormatDistanceParams): string {
  const distanceInMeters = 'miles' in distance ? milesToMeters(distance.miles) : distance.meters

  if (distanceInMeters < 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'unit',
      unit: 'meter',
      unitDisplay,
      maximumFractionDigits: 0,
    }).format(Math.round(distanceInMeters))
  }

  const distanceInKm = distanceInMeters / 1000

  return new Intl.NumberFormat('en-US', {
    style: 'unit',
    unit: 'kilometer',
    unitDisplay,
    maximumFractionDigits: getFractionDigits(distanceInKm),
  }).format(distanceInKm)
}

function getFractionDigits(distance: number): number {
  return distance < 10 ? 1 : 0
}
